/**
 * 最小 IndexedDB 封装。
 *
 * 不引 Dexie(~25KB)—— 需求只是几个 store 的增删查,
 * 而司机端每 KB 首包都要算。照片 Blob 也存这里,
 * localStorage 装不下(5MB 上限,一张压缩后照片就 200KB)。
 */

const DB_NAME = 'delivery-driver';
/** v2:新增 cache store。**离线读缓存必须和 outbox 分开** ——
 *  混在一个 store 里,`outbox.list()` 会把缓存条目当成待同步的操作 */
const DB_VERSION = 2;

const OUTBOX = 'outbox';
const CACHE = 'cache';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(OUTBOX)) {
        const store = db.createObjectStore(OUTBOX, { keyPath: 'id' });
        // 按订单分组做串行同步:同一单的取货必须在送达之前到达服务器
        store.createIndex('orderId', 'orderId');
        store.createIndex('createdAt', 'createdAt');
      }
      if (!db.objectStoreNames.contains(CACHE)) {
        db.createObjectStore(CACHE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function makeStore(name: string) {
  async function run<T>(
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const db = await open();
    return new Promise((resolve, reject) => {
      const t = db.transaction(name, mode);
      const req = fn(t.objectStore(name));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  return {
    put: <T>(value: T): Promise<IDBValidKey> => run('readwrite', (s) => s.put(value)),
    get: <T>(id: string): Promise<T | undefined> => run('readonly', (s) => s.get(id)),
    all: <T>(): Promise<T[]> => run('readonly', (s) => s.getAll()),
    delete: (id: string): Promise<undefined> => run('readwrite', (s) => s.delete(id)),
    clear: (): Promise<undefined> => run('readwrite', (s) => s.clear()),
  };
}

/** 待同步的写操作 */
export const idb = makeStore(OUTBOX);

/** 离线可读的任务数据 */
export const idbCache = makeStore(CACHE);
